import React, { FC, useState, Fragment } from 'react';

import { calculateOveralMatchResult, getLowestNetScoreUsingMatchShots, getResultDisplayColorTextForOverAllResult, isMatchFinished } from '../../../../utils/score-calculator';
import { Confirmation } from './confirmation';
import * as CSS from '../../match-entry-styles';
import { Course, Match, PlayerResult } from '../../../../../database.schema';
import { HoleScore } from '../hole-score';
import { FlexColumnCentred, FlexRowCentred, FlexRowSpaceBetween } from '../../../common-styles/common-styles';
import { BACK_NINE_INDEX, FRONT_NINE_INDEX } from '../../../../app/constants';

type Props = {
  recordedMatch: Match;
  course: Course;
  setRecordedMatch: (value: Match) => void;
  setShowScoreEntry: (value: boolean) => void;
  startingHole: number;
};

export const HoleScores: FC<Props> = ({ setShowScoreEntry, recordedMatch, course, setRecordedMatch, startingHole }): JSX.Element => {
  const [showConfirm, setShowConfirm] = useState(false);
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  const [holeIndex, setHoleIndex] = useState(0);
  const [holePar, setHolePar] = useState(course.scoreCard[holeIndexes[0]]);
  const [holeSI, setHoleSI] = useState(course.strokeIndex[holeIndexes[0]]);
  const [result, setResult] = useState('hole halved');
  const [recordedMatchStore, setRecordedMatchStore] = useState(recordedMatch);
  const [matchOverAllResult, setMatchOverAllResult] = useState('A/S');

  const setHoleData = (newIndex: number) => {
    setHoleIndex(newIndex);
    setHolePar(course.scoreCard[holeIndexes[newIndex]]);
    setHoleSI(course.strokeIndex[holeIndexes[newIndex]]);
  };

  const decrementHole = () => {
    const newIndex = holeIndex - 1;
    setHoleData(newIndex);
  };
  const incrementHole = () => {
    const newIndex = holeIndex + 1;
    setHoleData(newIndex);
    if (newIndex === 9) {
      setShowConfirm(!showConfirm);
    }
  };

  const getResultDisplayTextForTeam = (team: string) => {
    if (team === 't1') {
      if (result === 't1') {
        return 'WIN';
      }
      if (result === 't2') {
        return 'LOSS';
      }
    }
    if (team === 't2') {
      if (result === 't2') {
        return 'WIN';
      }
      if (result === 't1') {
        return 'LOSS';
      }
    }
    return 'HALF';
  };
  const calculateHoleResult = (recordedMatchData: Match) => {
    const resultByHoleClone = [...recordedMatchData.resultByHole];

    if (isMatchFinished(resultByHoleClone, holeIndex)) {
      resultByHoleClone[holeIndex] = '-';
    } else {
      const t1LowScore = getLowestNetScoreUsingMatchShots(recordedMatchData.team1.players, holeIndex, course.strokeIndex[holeIndexes[holeIndex]]);
      const t2LowScore = getLowestNetScoreUsingMatchShots(recordedMatchData.team2.players, holeIndex, course.strokeIndex[holeIndexes[holeIndex]]);
      if (t1LowScore === t2LowScore) {
        setResult('half');
        resultByHoleClone[holeIndex] = 'h';
      }
      if (t1LowScore < t2LowScore) {
        setResult('t1');
        resultByHoleClone[holeIndex] = 't1';
      }
      if (t1LowScore > t2LowScore) {
        setResult('t2');
        resultByHoleClone[holeIndex] = 't2';
      }
    }
    setRecordedMatchStore({ ...recordedMatchData, resultByHole: resultByHoleClone });
    setMatchOverAllResult(`${calculateOveralMatchResult(resultByHoleClone.slice(0, holeIndex + 1))} thru ${holeIndex + 1}`);
  };

  const setPlayerData = (playerId: number, recordedScore: number, team: string) => {
    if (team === 'team1') {
      const playerIndex = recordedMatchStore.team1.players.findIndex((player: PlayerResult) => player.playerId === playerId);
      const round = recordedMatchStore.team1.players[playerIndex].round;
      if (round) {
        const newScoreCard = round.scoreCard ? [...round.scoreCard] : [];
        newScoreCard.splice(holeIndex, 1, recordedScore);
        const newTeam1Player = { ...recordedMatchStore.team1.players[playerIndex], round: { ...round, scoreCard: newScoreCard } };
        const players = [...recordedMatchStore.team1.players];
        players.splice(playerIndex, 1, newTeam1Player);
        const newRecordedMatch = { ...recordedMatchStore, team1: { ...recordedMatchStore.team1, players } };
        calculateHoleResult(newRecordedMatch);
      }
    }
    if (team === 'team2') {
      const playerIndex = recordedMatchStore.team2.players.findIndex((player: PlayerResult) => player.playerId === playerId);
      const round = recordedMatchStore.team2.players[playerIndex].round;
      if (round) {
        const newScoreCard = round.scoreCard ? [...round.scoreCard] : [];
        newScoreCard.splice(holeIndex, 1, recordedScore);
        const newTeam2Player = { ...recordedMatchStore.team2.players[playerIndex], round: { ...round, scoreCard: newScoreCard } };
        const players = [...recordedMatchStore.team2.players];
        players.splice(playerIndex, 1, newTeam2Player);
        const newRecordedMatch = { ...recordedMatchStore, team2: { ...recordedMatchStore.team2, players } };
        calculateHoleResult(newRecordedMatch);
      }
    }
  };

  const saveScoresAndShowConfirm = () => {
    setRecordedMatch({ ...recordedMatchStore });
    setShowConfirm(true);
  };
  if (showConfirm) {
    return (
      <Confirmation
        startingHole={startingHole}
        recordedMatch={recordedMatchStore}
        holeIndex={holeIndex}
        course={course}
        setShowConfirm={setShowConfirm}
        setShowScoreEntry={setShowScoreEntry}
        decrementHole={decrementHole}
      />
    );
  }

  return (
    <Fragment>
      <h2>Score Entry</h2>
      <FlexRowSpaceBetween>
        <h3>Course: {course.name}</h3>
      </FlexRowSpaceBetween>
      <CSS.ScoreEntryWrapper data-testid="score-entry">
        <CSS.MiddleSection>
          <CSS.HoleInfoTitle>Hole</CSS.HoleInfoTitle>
          <CSS.FlexRow>
            <CSS.HoleNavigationButton onClick={() => decrementHole()} disabled={holeIndex === 0}>
              {`<< previous`}
            </CSS.HoleNavigationButton>
            <CSS.HoleNumber>{holeIndexes[holeIndex] + 1}</CSS.HoleNumber>{' '}
            {holeIndex < 17 && (
              <CSS.HoleNavigationButton data-testid="increment-hole" onClick={() => incrementHole()} disabled={holeIndex === 17}>
                {`next >>`}
              </CSS.HoleNavigationButton>
            )}
            {holeIndex === 17 && (
              <CSS.HoleNavigationButton data-testid="finish-button" onClick={() => saveScoresAndShowConfirm()}>
                FINISH
              </CSS.HoleNavigationButton>
            )}
          </CSS.FlexRow>
          <CSS.FlexRow>
            <CSS.HoleInfoTitle>{`Par: ${holePar}`}</CSS.HoleInfoTitle>
            <CSS.HoleInfoTitle>
              SI: <CSS.SI>{holeSI}</CSS.SI>
            </CSS.HoleInfoTitle>
          </CSS.FlexRow>
          <CSS.DivWithMargin borderColour="red">
            <FlexRowSpaceBetween style={{ alignItems: 'stretch' }}>
              <CSS.VerticalText bgColour="red">{getResultDisplayTextForTeam('t1')}</CSS.VerticalText>
              <FlexColumnCentred>
                {recordedMatchStore.team1.players.map((player: PlayerResult) => (
                  <HoleScore
                    key={`key-${player.playerId}`}
                    playerId={player.playerId}
                    handicap={player.round.individualHandicap}
                    strokeIndex={holeSI}
                    par={holePar}
                    score={player.round.scoreCard[holeIndex]}
                    setPlayerData={setPlayerData}
                    team="team1"
                    matchShots={player.round.shotsReceived}
                  />
                ))}
              </FlexColumnCentred>
            </FlexRowSpaceBetween>
          </CSS.DivWithMargin>
          <FlexRowCentred>
            {matchOverAllResult.startsWith('--') && <CSS.ArrowLeft />}
            {matchOverAllResult.startsWith('t1') && <CSS.ArrowUp />}
            {matchOverAllResult.startsWith('t2') && <CSS.ArrowDown />}
            <CSS.MatchOverAllResult colour={getResultDisplayColorTextForOverAllResult(matchOverAllResult)}>{matchOverAllResult.substring(3)}</CSS.MatchOverAllResult>
            {matchOverAllResult.startsWith('t1') && <CSS.ArrowUp />}
            {matchOverAllResult.startsWith('t2') && <CSS.ArrowDown />}
            {matchOverAllResult.startsWith('--') && <CSS.ArrowRight />}
          </FlexRowCentred>
          <CSS.DivWithMargin borderColour="blue">
            <FlexRowSpaceBetween style={{ alignItems: 'stretch' }}>
              <CSS.VerticalText bgColour="blue">{getResultDisplayTextForTeam('t2')}</CSS.VerticalText>
              <FlexColumnCentred>
                {recordedMatchStore.team2.players.map((player: PlayerResult) => (
                  <HoleScore
                    key={`key-${player.playerId}`}
                    playerId={player.playerId}
                    handicap={player.round.individualHandicap}
                    strokeIndex={holeSI}
                    par={holePar}
                    score={player.round.scoreCard[holeIndex]}
                    setPlayerData={setPlayerData}
                    team="team2"
                    matchShots={player.round.shotsReceived}
                  />
                ))}
              </FlexColumnCentred>
            </FlexRowSpaceBetween>
          </CSS.DivWithMargin>
          <FlexRowCentred>
            <CSS.Asterisk>*</CSS.Asterisk>
            <div>match shot(s) </div>
          </FlexRowCentred>
        </CSS.MiddleSection>
      </CSS.ScoreEntryWrapper>
    </Fragment>
  );
};
